/**
 * ч.1410: платформа → «Одиниці виміру» (ДК 011-96). Системний довідник: його читають усі акаунти.
 * Галочка «у короткому списку» піднімає одиницю нагору у виборі на картці товару, решта знаходиться
 * пошуком. Одиницю, що вже стоїть у товарах чи документах, видалити не можна.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { platformRefs, type PlatformUom, type PlatformUomCreate } from '@/api/platformRefs';
import { useConfirm } from '@/components/ConfirmProvider';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

const EMPTY: PlatformUomCreate = {
  kspovo_code: null, name: '', short_name: '', international_code: null, section: null, is_common: false,
};
/** Більше рядків на екрані не потрібно: довідник великий, далі уточнюють пошук. */
const MAX_ROWS = 300;

function errText(e: unknown): string {
  const d = (e as AxiosError<{ detail?: unknown }>)?.response?.data?.detail;
  return typeof d === 'string' ? d : 'Не вдалося зберегти';
}

function toPayload(u: PlatformUom): PlatformUomCreate {
  return {
    kspovo_code: u.kspovo_code, name: u.name, short_name: u.short_name,
    international_code: u.international_code, section: u.section, is_common: u.is_common,
  };
}

export default function UomClassifierTab() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const KEY = ['platform-uoms'];
  const list = useQuery({ queryKey: KEY, queryFn: platformRefs.uoms.list });
  const [q, setQ] = useState('');
  const [onlyCommon, setOnlyCommon] = useState(false);
  const [form, setForm] = useState<PlatformUomCreate | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = () => {
    setError(null);
    qc.invalidateQueries({ queryKey: KEY });
    qc.invalidateQueries({ queryKey: ['uoms'] });
    qc.invalidateQueries({ queryKey: ['uoms-for-picker'] });
  };
  const save = useMutation({
    mutationFn: () => (editId ? platformRefs.uoms.update(editId, form!) : platformRefs.uoms.create(form!)),
    onSuccess: () => { setForm(null); setEditId(null); refresh(); },
    onError: (e) => setError(errText(e)),
  });
  const toggle = useMutation({
    mutationFn: (u: PlatformUom) => platformRefs.uoms.update(u.id, { ...toPayload(u), is_common: !u.is_common }),
    onSuccess: refresh,
    onError: (e) => setError(errText(e)),
  });
  const remove = useMutation({
    mutationFn: (id: string) => platformRefs.uoms.remove(id),
    onSuccess: refresh,
    onError: (e) => setError(errText(e)),
  });

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (list.data ?? []).filter((u) => (!onlyCommon || u.is_common) && (!needle
      || u.name.toLowerCase().includes(needle) || u.short_name.toLowerCase().includes(needle)
      || (u.kspovo_code ?? '').includes(needle) || (u.section ?? '').toLowerCase().includes(needle)));
  }, [list.data, q, onlyCommon]);

  const cols = useMemo<ColumnDef<PlatformUom>[]>(() => [
    { key: 'kspovo', header: 'Код', width: 80, sortValue: (u) => u.kspovo_code ?? '', cell: (u) => u.kspovo_code ?? '—' },
    { key: 'name', header: 'Назва', width: 340, sortValue: (u) => u.name, cell: (u) => u.name },
    { key: 'short', header: 'Позначення', width: 130, sortValue: (u) => u.short_name, cell: (u) => u.short_name },
    { key: 'section', header: 'Розділ ДК', width: 170, sortValue: (u) => u.section ?? '', cell: (u) => u.section ?? '' },
    { key: 'used', header: 'Товарів', width: 80, align: 'right', sortValue: (u) => u.nomenclature_count, cell: (u) => u.nomenclature_count },
    { key: 'common', header: 'Короткий список', width: 100, align: 'center', sortValue: (u) => Number(u.is_common),
      cell: (u) => (
        <input type="checkbox" checked={u.is_common} disabled={toggle.isPending}
          aria-label={`У короткому списку: ${u.name}`} onChange={() => toggle.mutate(u)} />
      ) },
    { key: 'act', header: '', width: 170, align: 'right', cell: (u) => (
      <span style={{ display: 'inline-flex', gap: '0.5rem' }}>
        <button type="button" className="btn-link"
          onClick={() => { setEditId(u.id); setForm(toPayload(u)); setError(null); }}>редагувати</button>
        <button type="button" className="btn-link" disabled={u.nomenclature_count > 0}
          title={u.nomenclature_count > 0 ? 'Одиниця використовується у товарах' : undefined}
          onClick={async () => {
            if (await confirm({ title: 'Видалити одиницю', message: `Видалити «${u.name}»?`, okLabel: 'видалити', cancelLabel: 'скасувати', danger: true })) {
              remove.mutate(u.id);
            }
          }}>видалити</button>
      </span>
    ) },
  ], [confirm, remove, toggle]);

  const set = (patch: Partial<PlatformUomCreate>) => setForm((f) => ({ ...(f ?? EMPTY), ...patch }));
  const kspovoOk = !form?.kspovo_code || /^\d{4}$/.test(form.kspovo_code);

  return (
    <div>
      <div className="toolbar" style={{ marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <span style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
          Системний довідник ДК 011-96 для всіх акаунтів: {list.data?.length ?? 0} одиниць
        </span>
        <button type="button" className="primary" style={{ marginLeft: 'auto' }}
          onClick={() => { setEditId(null); setForm({ ...EMPTY }); setError(null); }}>+ додати одиницю</button>
      </div>
      {error && <div className="mc__panel mc__panel--warn" role="alert">{error}</div>}
      {form && (
        <div className="card" data-testid="uom-form">
          <h3 style={{ marginTop: 0 }}>{editId ? 'Редагування одиниці' : 'Нова одиниця'}</h3>
          <div className="doc-grid">
            <div className="field" style={{ gridColumn: 'span 3' }}>
              <label htmlFor="uom-kspovo">Код КСПОВО (4 цифри)</label>
              <input id="uom-kspovo" value={form.kspovo_code ?? ''} inputMode="numeric"
                onChange={(e) => set({ kspovo_code: e.target.value.trim() || null })} />
            </div>
            <div className="field" style={{ gridColumn: 'span 5' }}>
              <label htmlFor="uom-name">Назва</label>
              <input id="uom-name" value={form.name} onChange={(e) => set({ name: e.target.value })} />
            </div>
            <div className="field" style={{ gridColumn: 'span 2' }}>
              <label htmlFor="uom-short">Позначення</label>
              <input id="uom-short" value={form.short_name} onChange={(e) => set({ short_name: e.target.value })} />
            </div>
            <div className="field" style={{ gridColumn: 'span 2' }}>
              <label htmlFor="uom-intl">Міжнародне</label>
              <input id="uom-intl" value={form.international_code ?? ''}
                onChange={(e) => set({ international_code: e.target.value.trim() || null })} />
            </div>
          </div>
          <label style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', margin: '0.5rem 0' }}>
            <input type="checkbox" checked={form.is_common} onChange={(e) => set({ is_common: e.target.checked })} />
            У короткому списку
          </label>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button type="button" className="primary" onClick={() => save.mutate()}
              disabled={save.isPending || !form.name.trim() || !form.short_name.trim() || !kspovoOk}>зберегти</button>
            <button type="button" onClick={() => { setForm(null); setEditId(null); setError(null); }}>скасувати</button>
          </div>
        </div>
      )}
      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', margin: '0.75rem 0' }}>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Пошук: назва, позначення, код, розділ"
          aria-label="Пошук одиниці" style={{ flex: '1 1 auto', maxWidth: 420 }} />
        <label style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
          <input type="checkbox" checked={onlyCommon} onChange={(e) => setOnlyCommon(e.target.checked)} />
          лише короткий список
        </label>
        {filtered.length > MAX_ROWS && (
          <span style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
            показано {MAX_ROWS} із {filtered.length}, уточніть пошук
          </span>
        )}
      </div>
      <DataTable<PlatformUom> rows={filtered.slice(0, MAX_ROWS)} columns={cols} rowKey={(u) => u.id}
        loading={list.isLoading} emptyText="Нічого не знайдено." />
    </div>
  );
}
